import React from "react";
import { useEffect, useContext, useState } from "react";
import Select, { components } from "react-select";
import { useAuth } from "../context/AuthContext";
import {
  getMandatoryFields,
  updateMandatoryFieldsBulk,
} from "../api/ValidationApi";
import { ITEM_CATEGORIES } from "../constants/itemCategories";
import toast from "react-hot-toast";

export default function CustomFieldsSection({
  product,
  setProduct,
  mandatoryFields,
  setMandatoryFields,
  msrpCadPerKg,
  msrpUsdPerKg,
}) {
  const ALLERGEN_OPTIONS = [
    { key: "mustard", en: "Mustard", fr: "Moutarde" },
    { key: "sesame", en: "Sesame", fr: "Sesame" },
    { key: "celery", en: "Celery", fr: "Celeri" },
    { key: "dairy", en: "Dairy", fr: "Lait" },
  ];

  const { user, canWrite } = useAuth();
  const token = localStorage.getItem("token");

  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const canEdit = (overrideState) => isAdmin && overrideState;
  // const canManageMandatoryFields =isAdmin || canWrite("CostingCustomFieldsMandatory");
  const canManageMandatoryFields = isAdmin;

  const [overrideCategory, setOverrideCategory] = useState(false);

  const [lengthOverride, setLengthOverride] = useState(false);
  const [overrideMsrpCad, setOverrideMsrpCad] = useState(false);
  const [overrideMsrpUsd, setOverrideMsrpUsd] = useState(false);
  const isIngredientType = product.type === "ingredient";
  const isBaseBlend = !isIngredientType;
  const productTypeKey = isIngredientType ? "ingredient" : "blend";
  const allergensOverrideEnabled = Boolean(product.allergensOverride);
  const ingredientFieldsAreMandatory =
    !isIngredientType || product.containsIngredients === "yes";
  const allergenFieldsAreMandatory = isIngredientType
    ? product.containsAllergen === "yes"
    : product.containsAllergen === "yes" || allergensOverrideEnabled;
  const areIngredientFieldsDisabled =
    isIngredientType && product.containsIngredients !== "yes";
  const areAllergenFieldsDisabled = isIngredientType
    ? product.containsAllergen !== "yes"
    : product.containsAllergen !== "yes" && !allergensOverrideEnabled;
  const canOverrideAllergens =
    isBaseBlend && product.containsAllergen !== "yes";
  const selectedAllergenKeys = Array.isArray(product.selectedAllergens)
    ? product.selectedAllergens
    : [];
  const allergenSelectOptionsEn = ALLERGEN_OPTIONS.map((option) => ({
    value: option.key,
    label: option.en,
    en: option.en,
    fr: option.fr,
  }));
  const allergenSelectOptionsFr = ALLERGEN_OPTIONS.map((option) => ({
    value: option.key,
    label: option.fr,
    en: option.en,
    fr: option.fr,
  }));
  const selectedAllergenOptionsEn = allergenSelectOptionsEn.filter((option) =>
    selectedAllergenKeys.includes(option.value),
  );
  const selectedAllergenOptionsFr = allergenSelectOptionsFr.filter((option) =>
    selectedAllergenKeys.includes(option.value),
  );

  useEffect(() => {
    const selectedCategory = ITEM_CATEGORIES.find(
      (cat) => cat.en === product.itemCategory,
    );

    setProduct((prev) => ({
      ...prev,

      nameEnCustom: prev.nameEn || "",
      nameFrCustom: prev.nameFr || "",

      rawMaterialSku: prev.sku || "",
      // fr: prev.descriptionFr || "",
      fr: prev.nameFr || "",

      categorie: selectedCategory ? selectedCategory.fr : "",
    }));
  }, [
    product.nameEn,
    product.nameFr,
    product.sku,
    product.descriptionFr,
    product.itemCategory,
  ]);

  const [loadingMandatory, setLoadingMandatory] = useState(true);

  useEffect(() => {
    const fetchMandatoryFields = async () => {
      try {
        const data = await getMandatoryFields(productTypeKey);
        setMandatoryFields(data || {});
      } catch (err) {
        console.error("Error loading mandatory fields:", err);
      } finally {
        setLoadingMandatory(false);
      }
    };

    if (token) {
      fetchMandatoryFields();
    }
  }, [token, productTypeKey, setMandatoryFields]);
  const saveMandatoryFields = async () => {
    if (!canManageMandatoryFields) {
      toast.error("You do not have permission to manage mandatory fields.");
      return;
    }

    try {
      const fieldsArray = Object.entries(mandatoryFields).map(
        ([field, value]) => ({
          field,
          value,
        }),
      );

      await updateMandatoryFieldsBulk(fieldsArray, productTypeKey);
      toast.success("Mandatory fields saved successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save mandatory fields");
    }
  };

  const CheckboxOption = (props) => {
    return (
      <components.Option {...props}>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={props.isSelected}
            readOnly
            style={{ accentColor: "#000000" }}
          />
          <span>{props.label}</span>
        </div>
      </components.Option>
    );
  };

  const allergenSelectStyles = {
    option: (base, state) => ({
      ...base,
      backgroundColor:
        state.isFocused || state.isSelected ? "#f3f4f6" : "#ffffff",
      color: "#111827",
      ":active": {
        ...base[":active"],
        backgroundColor: "#e5e7eb",
      },
    }),
  };

  const handleAllergenMultiChange = (selectedOptions) => {
    const safeOptions = Array.isArray(selectedOptions) ? selectedOptions : [];
    const selectedKeys = safeOptions.map((option) => option.value);

    setProduct((prev) => ({
      ...prev,
      selectedAllergens: selectedKeys,
      allergensEn: safeOptions.map((a) => a.en).join(", "),
      allergensFr: safeOptions.map((a) => a.fr).join(", "),
    }));
  };

  const allergenInputClassName = `w-full px-4 py-3 border rounded-lg ${areAllergenFieldsDisabled
    ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-200"
    : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
    }`;

  useEffect(() => {
    const shouldClearAllergens =
      (isIngredientType && product.containsAllergen !== "yes") ||
      (!isIngredientType &&
        product.containsAllergen !== "yes" &&
        !allergensOverrideEnabled);

    if (!shouldClearAllergens) return;

    const hasAllergenValues =
      (Array.isArray(product.selectedAllergens) &&
        product.selectedAllergens.length > 0) ||
      product.allergensEn ||
      product.allergensFr;

    if (!hasAllergenValues) return;

    setProduct((prev) => ({
      ...prev,
      selectedAllergens: [],
      allergensEn: "",
      allergensFr: "",
    }));
  }, [isIngredientType, product.containsAllergen, allergensOverrideEnabled]);

  const FieldLabel = ({ label, fieldKey }) => {
    const isMandatory = fieldKey ? mandatoryFields[fieldKey] : false;

    return (
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <span>{label}</span>
        {isMandatory && <span className="text-red-500">*</span>}

        {/* {canManageMandatoryFields && fieldKey && ( */}
        {isAdmin && fieldKey && (
          <input
            type="checkbox"
            checked={!!mandatoryFields[fieldKey]}
            className="ml-2 cursor-pointer"
            aria-label={`Toggle ${label} mandatory`}
            onChange={(e) => {
              const newValue = e.target.checked;

              setMandatoryFields((prev) => ({
                ...prev,
                [fieldKey]: newValue,
              }));
            }}
          />
        )}
      </div>
    );
  };

  const mapMetricToImperialUnit = (unit) => {
    if (unit === "g") return "oz";
    if (unit === "kg") return "lbs";
    return "";
  };

  const convertMetricToImperial = (value, unit) => {
    if (!value) return "";

    const num = Number(value);
    if (Number.isNaN(num)) return "";

    if (unit === "g") return (num * 0.035274).toFixed(2); // g → oz
    if (unit === "kg") return (num * 2.20462).toFixed(2); // kg → lbs

    return "";
  };
  useEffect(() => {
    if (!product.gkKgUnit) return;

    const newUnit = mapMetricToImperialUnit(product.gkKgUnit);
    const newValue = convertMetricToImperial(
      product.netWeightGkKg,
      product.gkKgUnit,
    );

    setProduct((prev) => ({
      ...prev,
      ozLbsUnit: newUnit,
      netWeightOzLbs: newValue,
    }));
  }, [product.netWeightGkKg, product.gkKgUnit]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6 md:p-8 min-w-0 w-full max-w-full overflow-hidden">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 sm:mb-8 gap-4">
        <h2 className="text-lg sm:text-xl md:text-2xl font-semibold">CUSTOM FIELDS</h2>

        {canManageMandatoryFields && (
          <button
            onClick={saveMandatoryFields}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-medium"
          >
            Save Mandatory Fields
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* FDA Code */}
        <div>
          <FieldLabel label="FDA Code" fieldKey="fdaCode" />
          {/* 
          <input
            type="text"
            placeholder="e.g. 28JGY99"
            className="mt-2 w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent uppercase"
            value={product.fdaCode}
            onChange={(e) =>
              setProduct({ ...product, fdaCode: e.target.value })
            }
          /> */}
          <input
            type="text"
            placeholder="e.g. 28JGY99"
            value={product.fdaCode}
            onChange={(e) =>
              setProduct({ ...product, fdaCode: e.target.value.toUpperCase() })
            }
            disabled={!isAdmin}
            className={`mt-2 w-full px-4 py-3 border rounded-lg uppercase ${!isAdmin
              ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
              : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              }`}
          />
        </div>

        {/* FR */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            FR
          </label>
          <input
            type="text"
            // value={product.descriptionFr || ""}
            value={product.nameFr || ""}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100"
          />
        </div>

        {/* Ingredients (EN) */}
        <div>
          <FieldLabel
            label="Ingredients (EN)"
            fieldKey={ingredientFieldsAreMandatory ? "ingredientsEn" : null}
          />

          <input
            type="text"
            placeholder="List ingredients"
            // className="mt-2 w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            disabled={!isAdmin}
            className={`mt-2 w-full px-4 py-3 border rounded-lg  ${!isAdmin
              ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
              : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              }`}
            value={product.ingredientsEn}
            onChange={(e) =>
              setProduct({ ...product, ingredientsEn: e.target.value })
            }
          // disabled={areIngredientFieldsDisabled}
          />
        </div>

        {/* ingrédients (FR) */}
        <div>
          <FieldLabel
            label="Ingredients (FR)"
            fieldKey={ingredientFieldsAreMandatory ? "ingredientsFr" : null}
          />

          <input
            type="text"
            placeholder="Liste des ingrédients"
            disabled={!isAdmin}
            className={`mt-2 w-full px-4 py-3 border rounded-lg  ${!isAdmin
              ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
              : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              }`}
            // className=" mt-2 w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            value={product.ingredientsFr}
            onChange={(e) =>
              setProduct({ ...product, ingredientsFr: e.target.value })
            }
          // disabled={areIngredientFieldsDisabled}
          />
        </div>

        {/* Allergens (EN) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <FieldLabel
              label="Allergens (EN)"
              fieldKey={allergenFieldsAreMandatory ? "allergensEn" : null}
            />
            {canOverrideAllergens && (
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={allergensOverrideEnabled}
                  onChange={(e) =>
                    setProduct({
                      ...product,
                      allergensOverride: e.target.checked,
                    })
                  }
                />
                <span>Override</span>
              </label>
            )}
          </div>

          {isIngredientType && product.containsAllergen === "yes" ? (
            <Select
              isMulti
              options={allergenSelectOptionsEn}
              value={selectedAllergenOptionsEn}
              onChange={handleAllergenMultiChange}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              isDisabled={areAllergenFieldsDisabled}
              placeholder="Select allergens"
              classNamePrefix="react-select"
              components={{ Option: CheckboxOption }}
              styles={allergenSelectStyles}
            />
          ) : (
            <input
              type="text"
              placeholder="List allergens"
              className={allergenInputClassName}
              value={product.allergensEn}
              onChange={(e) =>
                setProduct({ ...product, allergensEn: e.target.value })
              }
              disabled={areAllergenFieldsDisabled}
            />
          )}
        </div>

        {/* Allergènes (FR) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <FieldLabel
              label="Allergens (FR)"
              fieldKey={allergenFieldsAreMandatory ? "allergensFr" : null}
            />
            {canOverrideAllergens && (
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={allergensOverrideEnabled}
                  onChange={(e) =>
                    setProduct({
                      ...product,
                      allergensOverride: e.target.checked,
                    })
                  }
                />
                <span>Override</span>
              </label>
            )}
          </div>

          {isIngredientType && product.containsAllergen === "yes" ? (
            <Select
              isMulti
              options={allergenSelectOptionsFr}
              value={selectedAllergenOptionsFr}
              onChange={handleAllergenMultiChange}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              isDisabled={areAllergenFieldsDisabled}
              placeholder="Liste des allergènes"
              classNamePrefix="react-select"
              components={{ Option: CheckboxOption }}
              styles={allergenSelectStyles}
            />
          ) : (
            <input
              type="text"
              placeholder="Liste des allergènes"
              className={allergenInputClassName}
              value={product.allergensFr}
              onChange={(e) =>
                setProduct({ ...product, allergensFr: e.target.value })
              }
              readOnly={isIngredientType && product.containsAllergen === "yes"}
              disabled={areAllergenFieldsDisabled}
            />
          )}
        </div>
        {/* Name EN (Custom) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Name EN
          </label>
          <input
            type="text"
            value={product.nameEnCustom}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100"
          />
        </div>
        {/* Name FR (Custom) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Name FR
          </label>
          <input
            type="text"
            value={product.nameFrCustom}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100"
          />
        </div>

        {/* Raw Material SKU */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Raw Material SKU
          </label>
          <input
            type="text"
            value={product.rawMaterialSku}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 font-mono"
          />
        </div>

        {/* Qty/SKU with unit dropdown */}
        <div>
          <FieldLabel label="Qty/SKU" fieldKey="qtySku" />

          <div className="flex gap-2">
            <input
              type="number"
              min="1"
              step="1"
              placeholder="Quantity"
              // disabled={!isAdmin}
              disabled={!isAdmin}
              className={`mt-2 w-full px-4 py-3 border rounded-lg uppercase 
              ${!isAdmin
                  ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
                  : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                }`}
              // className="mt-2 flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              value={product.qtySku}
              onChange={(e) =>
                setProduct({ ...product, qtySku: e.target.value })
              }
              readOnly={true}
            />
            <select
              className=" mt-2 w-24 px-2 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              value={product.qtySkuUnit || "un."}
              onChange={(e) =>
                setProduct({ ...product, qtySkuUnit: e.target.value })
              }
            >
              <option value="kg">kg</option>
              <option value="CS">CS</option>
              <option value="g">g</option>
            </select>
          </div>
        </div>

        {/* Net Weight (g/kg) */}
        <div>
          <FieldLabel label="Net Weight 1 " fieldKey="netWeightGkKg" />

          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              placeholder="Weight"
              // className=" mt-2 flex-1 px-4 py-3 border border-gray-300 rounded-lg"
              disabled={!isAdmin}
              className={`mt-2 w-full px-4 py-3 border rounded-lg uppercase ${!isAdmin
                ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
                : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                }`}
              value={product.netWeightGkKg}

              onChange={(e) =>
                setProduct({ ...product, netWeightGkKg: e.target.value })
              }
              readOnly={true}
            />
            <select
              className="mt-2 w-24 px-2 py-3 border border-gray-300 rounded-lg"
              value={product.gkKgUnit}
              onChange={(e) =>
                setProduct({ ...product, gkKgUnit: e.target.value })
              }
            >
              <option value="g">g</option>
              <option value="kg">kg</option>
            </select>
          </div>
        </div>

        {/* Net Weight (oz/lbs) */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Net Weight 2
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              placeholder="Weight"
              className=" mt-2 flex-1 px-4 py-3 border border-gray-300 rounded-lg"
              value={product.netWeightOzLbs}
              onChange={(e) =>
                setProduct({ ...product, netWeightOzLbs: e.target.value })
              }
              readOnly={true}
            />
            <input
              type="text"
              value={product.ozLbsUnit}
              readOnly
              className="mt-2 w-24 px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-center font-medium"
              tabIndex={-1}
            />
          </div>
        </div>

        {/* Catégorie */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Catégorie
          </label>
          <input
            type="text"
            placeholder="Category"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            value={product.categorie}
            onChange={(e) =>
              setProduct({ ...product, categorie: e.target.value })
            }
          />
        </div>

        {/* MSRP CAD */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <FieldLabel label="MSRP CAD" fieldKey="msrpCad" />
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={overrideMsrpCad}
                  onChange={(e) => setOverrideMsrpCad(e.target.checked)}
                />
                <span>Override</span>
              </label>
            )}
          </div>


          {/* <input
            type="number"
            step="0.01"
            min="0"
            placeholder="Auto generated MSRP CAD"
            className={`w-full px-4 py-3 border rounded-lg ${overrideMsrpCad
              ? "border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent bg-white"
              : "bg-gray-100 cursor-not-allowed border-gray-200 text-gray-700"
              }`}
            // value={product.msrpCad}
             value={msrpUsdPerKg ?? ""}
            // value={product.msrpCad ? (Number(product.msrpCad) / 25).toFixed(4) : ""}
            onChange={(e) => {
              if (overrideMsrpCad) {
                setProduct({ ...product, msrpCad: e.target.value });
              }
            }}
            readOnly={!overrideMsrpCad && isAdmin}
          /> */}
          {/* <p className="text-xs text-gray-500 mt-1">
  Per kg: <span className="font-semibold text-gray-700">${msrpUsdPerKg ?? "0.000"}</span>
</p> */}
          <input
            type="text"
            step="0.01"
            min="0"
            placeholder="Auto generated MSRP CAD"
            className={`w-full px-4 py-3 border rounded-lg ${overrideMsrpCad
              ? "border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent bg-white"
              : "bg-gray-100 cursor-not-allowed border-gray-200 text-gray-700"
              }`}
            value={
              product.msrpCad
                ? isIngredientType
                  ? String(product.msrpCad)
                  : Number(msrpCadPerKg ?? 0).toFixed(2)
                : ""
            }
            onChange={(e) => {
              if (overrideMsrpCad) {
                setProduct({ ...product, msrpCad: e.target.value });
              }
            }}
            readOnly={!overrideMsrpCad && isAdmin}
          />

        </div>

        {/* MSRP USD */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <FieldLabel label="MSRP USD" fieldKey="msrpUsd" />
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={overrideMsrpUsd}
                  onChange={(e) => setOverrideMsrpUsd(e.target.checked)}
                />
                <span>Override</span>
              </label>
            )}
          </div>

          <input
            type="text"
            step="0.01"
            min="0"
            placeholder="Auto generated MSRP USD"
            className={`w-full px-4 py-3 border rounded-lg ${overrideMsrpUsd && isAdmin
              ? "border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent bg-white"
              : "bg-gray-100 cursor-not-allowed border-gray-200 text-gray-700"
              }`}
            value={
              product.msrpUsd
                ? isIngredientType
                  ? String(product.msrpUsd)
                  : !isNaN(msrpUsdPerKg)
                  ? Number(msrpUsdPerKg).toFixed(2)
                  : "0.00"
                : ""
            }
            onChange={(e) => {
              if (overrideMsrpUsd && isAdmin) {
                setProduct({ ...product, msrpUsd: e.target.value });
              }
            }}
            readOnly={!overrideMsrpUsd && isAdmin}
          />
        </div>

        {/* Dimensions - mm */}
        <div>
          <FieldLabel
            label="L(mm)"
            fieldKey={isIngredientType ? null : "L(mm)"}
          />
          <input
            type="number"
            min="0"
            placeholder="Auto-populated from selected packaging"
            // className="mt-2 w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-500"
            disabled={!isAdmin}
            className={`mt-2 w-full px-4 py-3 border rounded-lg bg-gray-100 ${!isAdmin
              ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
              : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              }`}
            value={product.lengthMm ?? ""}
            onChange={(e) =>
              setProduct({ ...product, lengthMm: e.target.value })
            }
            readOnly={true}
          />
        </div>

        <div>
          <FieldLabel
            label="W(mm)"
            fieldKey={isIngredientType ? null : "W(mm)"}
          />
          <input
            type="number"
            min="0"
            placeholder="Auto-populated from selected packaging"
            // className="mt-2 w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-500"
            disabled={!isAdmin}
            className={`mt-2 w-full px-4 py-3 border rounded-lg bg-gray-100  ${!isAdmin
              ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
              : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              }`}
            value={product.widthMm ?? ""}
            onChange={(e) =>
              setProduct({ ...product, widthMm: e.target.value })
            }

            readOnly={true}
          />
        </div>

        <div>
          <FieldLabel
            label="H(mm)"
            fieldKey={isIngredientType ? null : "H(mm)"}
          />
          <input
            type="number"
            min="0"
            placeholder="Auto-populated from selected packaging"
            // className="mt-2 w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-500"
            disabled={!isAdmin}
            className={`mt-2 w-full px-4 py-3 border rounded-lg bg-gray-100 ${!isAdmin
              ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
              : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              }`}
            value={product.heightMm ?? ""}
            onChange={(e) =>
              setProduct({ ...product, heightMm: e.target.value })
            }
            // disabled={!isAdmin}
            readOnly={true}
          />
        </div>

        {/* Dimensions - inches */}
        <div>
          <FieldLabel
            label="L('')"
            fieldKey={isIngredientType ? null : "L('')"}
          />
          <input
            type="text"
            placeholder="Auto-converted from mm (packaging)"
            // className="mt-2 w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-500"
            disabled={!isAdmin}
            className={`mt-2 w-full px-4 py-3 border rounded-lg bg-gray-100  ${!isAdmin
              ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
              : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              }`}
            value={product.lengthInch ?? ""}
            onChange={(e) =>
              setProduct({ ...product, lengthInch: e.target.value })
            }
            // disabled={!(isAdmin && lengthOverride)}
            readOnly={true}
          />
        </div>

        <div>
          <FieldLabel
            label="W('')"
            fieldKey={isIngredientType ? null : "W('')"}
          />
          <input
            type="text"
            placeholder="Auto-converted from mm (packaging)"
            // className="mt-2 w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-500"
            disabled={!isAdmin && true}
            className={`mt-2 w-full px-4 py-3 border rounded-lg bg-gray-100  ${!isAdmin
              ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
              : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              }`}
            value={product.widthInch ?? ""}
            onChange={(e) =>
              setProduct({ ...product, widthInch: e.target.value })
            }
            // disabled={true}
            readOnly={true}
          />
        </div>

        <div>
          <FieldLabel
            label="H('')"
            fieldKey={isIngredientType ? null : "H('')"}
          />
          <input
            type="text"
            placeholder="Auto-converted from mm (packaging)"
            // className="mt-2 w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-500"
            disabled={!isAdmin}
            className={`mt-2 w-full px-4 py-3 border rounded-lg bg-gray-100  ${!isAdmin
              ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
              : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              }`}
            value={product.heightInch ?? ""}
            onChange={(e) =>
              setProduct({ ...product, heightInch: e.target.value })
            }
            // disabled={!isAdmin}
            readOnly={true}
          />
        </div>
      </div>
    </div>
  );
}
